/* eslint-disable @typescript-eslint/no-unused-vars */

import React, { createContext, useContext, useMemo } from 'react';

export type CrudAction =
  | 'create'
  | 'read'
  | 'update'
  | 'delete'
  | 'import'
  | 'export'
  | 'print'
  | 'filter'
  | 'setupFormQT';

export type RolePermission = {
  controller: string;
  action: string;
  create: boolean;
  read: boolean;
  update: boolean;
  delete: boolean;
  import: boolean;
  export: boolean;
  print: boolean;
  filter: boolean;
  setupFormQT: boolean;
  allBP: boolean;
  allCT: boolean;
};

export type AuthUser = {
  id: string;
  name: string;
  listRole: RolePermission[];
  isAdmin: boolean;
  isQuanLy: boolean;
  avatar: string;
  backGround: string;
  tanSuatGuiMail: string;
  boPhanId: string;
  childs: AuthUser[];
  chucVuId: string; 
};

type AuthContextType = {
  user: AuthUser | null;
  // check 1 quyền
  can: (
    controller: string,
    permission: CrudAction
  ) => boolean;

  // check nhiều quyền (OR)
  canAny: (
    controller: string,
    permissions: CrudAction[]
  ) => boolean;

  // lấy toàn bộ permission của 1 controller
  getPermissions: (controller: string) => Record<string, boolean> | null;
};

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ user: AuthUser | null; children: React.ReactNode }> = ({
  user,
  children,
}: {
  user: AuthUser | null;
  children: React.ReactNode;
}) => {
  const value = useMemo<AuthContextType>(() => {
    const findRole = (controller: string) =>
      user?.listRole.find(r => r.controller === controller);

    const can = (controller: string, permission: CrudAction) => {
      if (!user) return false;

      const role = findRole(controller);
      return !!role?.[permission];
    };

    const canAny = (controller: string, permissions: CrudAction[]) => {
      return permissions.some(p => can(controller, p));
    };

    const getPermissions = (controller: string) => {
      const role = findRole(controller);
      if (!role) return null;

      const {
        controller: _,
        action: __,
        ...permissions
      } = role;

      return permissions;
    };

    return {
      user,
      can,
      canAny,
      getPermissions,
    };
  }, [user]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return ctx;
};
